import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ITooltip } from '../../../models';
import './styles.scss';

type Rect = { top: number; left: number; width: number; height: number };

const GAP = 6;
const EDGE_MARGIN = 4;

/**
 * Lightweight hover tooltip, standing in for @mui/material's Tooltip (not a
 * project dependency here). Portaled to document.body and positioned via a
 * fixed-position bounding-rect calculation (same technique as the Select
 * menu) rather than plain CSS around a relative wrapper — a wrapper-relative
 * bubble gets clipped by any ancestor with `overflow` set to non-visible
 * (e.g. #bot-page's `overflow: hidden`). Recomputes on scroll/resize while
 * open and clamps to the viewport so it can't render off-screen or drift
 * from its anchor if the page scrolls underneath it.
 */
const Tooltip: FC<ITooltip> = ({
  children,
  title,
  placement = 'right',
  maxWidth,
  fontSize = '13px',
  disableHoverListener = false,
}) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);

  const updateRect = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);

  const show = useCallback(() => {
    updateRect();
    setOpen(true);
  }, [updateRect]);

  const hide = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  if (disableHoverListener || !title) return children;

  const bubbleStyle: React.CSSProperties = { fontSize, maxWidth: maxWidth || undefined };
  if (rect) {
    let left: number;
    let top: number;
    let transform: string;
    if (placement.startsWith('top')) {
      left = rect.left + rect.width / 2;
      top = rect.top - GAP;
      transform = 'translate(-50%, -100%)';
    } else if (placement.startsWith('bottom')) {
      left = rect.left + rect.width / 2;
      top = rect.top + rect.height + GAP;
      transform = 'translateX(-50%)';
    } else if (placement.startsWith('left')) {
      left = rect.left - GAP;
      top = rect.top + rect.height / 2;
      transform = 'translate(-100%, -50%)';
    } else {
      left = rect.left + rect.width + GAP;
      top = rect.top + rect.height / 2;
      transform = 'translateY(-50%)';
    }
    // Clamp so the anchor point itself never lands off-screen — the bubble
    // can still be nudged by its own transform, but this keeps it from
    // rendering somewhere nonsensical when the anchor sits near an edge.
    left = Math.min(Math.max(left, EDGE_MARGIN), window.innerWidth - EDGE_MARGIN);
    top = Math.min(Math.max(top, EDGE_MARGIN), window.innerHeight - EDGE_MARGIN);
    bubbleStyle.left = left;
    bubbleStyle.top = top;
    bubbleStyle.transform = transform;
  }

  return (
    <span
      ref={anchorRef}
      className="kai-tooltip-anchor"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open &&
        rect &&
        createPortal(
          <span className="kai-tooltip-bubble" style={bubbleStyle}>
            {title}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default memo(Tooltip);
